import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import ShareModal from '../components/ui/share-modal';
import RequestSetupModal from '../components/ui/request-setup-modal';
import { 
  Mic, Trophy, Truck, Banknote, ArrowRight, CheckCircle2, 
  FileText, Camera, Users, ClipboardCheck, ShieldCheck, 
  Calculator, Bell, MessageSquare, Share2, Brain, Send, Smartphone, ChevronDown
} from 'lucide-react';

// Mobile Voice Demo Component - shows mic with rotating quotes
const MobileVoiceDemo = () => {
  const quotes = [
    { text: "Met with Sarah at the new build site. She wants a quote for full electrical...", color: "bg-emerald-500/80" },
    { text: "Finished installing the main switchboard today. Waiting on the plumber...", color: "bg-blue-500/80" },
    { text: "Builder requested extra power points in the garage. That's a variation...", color: "bg-orange-500/80" }
  ];
  
  const [currentQuote, setCurrentQuote] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  
  useEffect(() => {
    let index = 0;
    let timeout;
    const text = quotes[currentQuote].text;
    
    const typeText = () => {
      if (index < text.length) {
        setDisplayedText(text.slice(0, index + 1));
        index++;
        timeout = setTimeout(typeText, 40 + Math.random() * 20);
      } else {
        setIsTyping(false);
        // Move to next quote after pause
        timeout = setTimeout(() => {
          setCurrentQuote((prev) => (prev + 1) % quotes.length);
          setDisplayedText('');
          index = 0;
          setIsTyping(true);
        }, 2500);
      }
    };
    
    timeout = setTimeout(typeText, 500);
    return () => clearTimeout(timeout);
  }, [currentQuote]);

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="flex items-center gap-5 ml-1"
    >
      {/* Mic button - matching desktop style */}
      <div className="relative flex-shrink-0">
        <motion.div 
          className="w-20 h-20 bg-orange-500 rounded-xl flex items-center justify-center shadow-[5px_5px_0px_rgba(0,0,0,0.3)]"
          animate={{ scale: isTyping ? [1, 1.03, 1] : 1 }}
          transition={{ duration: 0.8, repeat: isTyping ? Infinity : 0 }}
        >
          <Mic className="w-10 h-10 text-white" strokeWidth={1.5} />
        </motion.div>
        
        {/* Pulse rings */}
        <motion.div 
          className="absolute inset-0 bg-orange-500/20 rounded-xl"
          animate={{ scale: [1, 1.3], opacity: [0.5, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
        
        {/* Corner accents - all 4 corners like desktop */}
        <div className="absolute -top-2 -left-2 w-4 h-4 border-t-2 border-l-2 border-orange-400"></div>
        <div className="absolute -top-2 -right-2 w-4 h-4 border-t-2 border-r-2 border-orange-400"></div>
        <div className="absolute -bottom-2 -left-2 w-4 h-4 border-b-2 border-l-2 border-orange-400"></div>
        <div className="absolute -bottom-2 -right-2 w-4 h-4 border-b-2 border-r-2 border-orange-400"></div>
        
        {/* TAP. TALK. DONE. label */}
        <p className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-bold text-orange-400 tracking-widest whitespace-nowrap">TAP. TALK. DONE.</p>
      </div>
      
      {/* Quote bubble - to the right, with more spacing */}
      <div className={`${quotes[currentQuote].color} rounded-lg px-3 py-2 max-w-[170px] relative`}>
        <div className={`absolute top-1/2 -left-2 -translate-y-1/2 w-3 h-3 ${quotes[currentQuote].color} rotate-45`}></div>
        <p className="text-white/90 text-[11px] font-medium min-h-[32px] relative z-10 leading-relaxed">
          "{displayedText}{isTyping && <span className="animate-pulse">|</span>}"
        </p>
      </div>
    </motion.div>
  );
};

// Animated Phone Mockup Component with typing effect
const AnimatedPhoneMockup = ({ imageSrc, imageAlt, typingText, accentColor }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  
  useEffect(() => {
    let index = 0;
    let timeout;
    
    const typeText = () => {
      if (index < typingText.length) {
        setDisplayedText(typingText.slice(0, index + 1));
        index++;
        timeout = setTimeout(typeText, 50 + Math.random() * 30);
      } else {
        setIsTyping(false);
        // Reset after pause
        timeout = setTimeout(() => {
          setDisplayedText('');
          index = 0;
          setIsTyping(true);
          typeText();
        }, 3000);
      }
    };
    
    timeout = setTimeout(typeText, 1000);
    return () => clearTimeout(timeout);
  }, [typingText]);

  return (
    <div className="relative">
      {/* Phone Frame */}
      <div className="relative w-[240px] sm:w-[280px] md:w-[300px] bg-slate-900 rounded-[2.5rem] p-2 shadow-2xl">
        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-20 h-5 bg-slate-900 rounded-full z-10"></div>
        <div className="rounded-[2rem] overflow-hidden bg-white relative">
          <img 
            src={imageSrc} 
            alt={imageAlt}
            className="w-full h-auto"
          />
        </div>
      </div>
      
      {/* Typing Text Below Phone - as a speech bubble/caption */}
      <div className="mt-4 max-w-[280px] mx-auto">
        <div className={`${accentColor} rounded-xl p-3 shadow-lg relative`}>
          {/* Speech bubble pointer */}
          <div className={`absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 ${accentColor} rotate-45`}></div>
          <p className="text-white text-sm font-medium min-h-[44px] relative z-10">
            "{displayedText}{isTyping && <span className="animate-pulse">|</span>}"
          </p>
        </div>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#1F2A37] rounded-lg flex items-center justify-center">
              <span className="text-orange-500 font-bold text-xl" style={{ fontFamily: 'Chivo, sans-serif' }}>2</span>
            </div>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>2TAP</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => navigate('/features')} className="text-slate-600 hover:text-slate-900 text-sm font-medium tracking-wide">FEATURES</button>
            <button 
              onClick={() => setShowShareModal(true)} 
              className="text-slate-600 hover:text-slate-900 text-sm font-medium tracking-wide flex items-center gap-1.5"
              data-testid="nav-share"
            >
              <Share2 className="w-4 h-4" />
              SHARE
            </button>
            <Button 
              onClick={() => navigate('/login')} 
              data-testid="nav-login"
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm px-6 font-semibold tracking-wide shadow-[3px_3px_0px_rgba(0,0,0,0.2)]"
            >
              LOG IN
            </Button>
          </div>
          <div className="md:hidden flex items-center gap-1">
            <button 
              onClick={() => navigate('/features')}
              className="text-slate-600 hover:text-slate-900 text-xs font-medium px-2"
              data-testid="nav-features-mobile"
            >
              FEATURES
            </button>
            <button 
              onClick={() => setShowShareModal(true)} 
              className="p-1.5 text-slate-600 hover:text-slate-900"
              data-testid="nav-share-mobile"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <Button 
              onClick={() => navigate('/login')} 
              size="sm" 
              data-testid="nav-login-mobile"
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-xs px-3"
            >
              LOG IN
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Dark with texture */}
      <section className="relative min-h-[92vh] sm:min-h-screen flex items-center pt-20 sm:pt-16 overflow-hidden">
        {/* Background texture */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/c4e7f5a3-280c-4676-af94-17c6255c2f72/images/58bef61957f0a16ed7eaa265f7cbf7f39055e9d10d8fa5a243edb78967a92571.png)' }}
        />
        <div className="absolute inset-0 bg-[#0f172a]/90" />
        
        <div className="relative max-w-7xl mx-auto px-4 pt-8 pb-12 sm:py-16 md:py-24 w-full">
          <div className="md:grid md:grid-cols-2 md:gap-12 md:items-center">
            {/* Left: Text */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-left"
            >
              <div className="inline-flex items-center gap-1.5 bg-orange-500/20 border border-orange-500/30 text-orange-400 px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 rounded-sm text-[10px] sm:text-xs md:text-sm font-semibold mb-2 sm:mb-4 md:mb-6 tracking-wider">
                <Smartphone className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4" />
                BUILT FOR TRADIES
              </div>
              
              {/* Headline */}
              <h1 
                className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-white leading-[1.1] tracking-tight uppercase mb-6 sm:mb-4 md:mb-6"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                YOUR OFFICE<br />
                <span className="text-orange-500">IN YOUR POCKET</span>
              </h1>
              
              {/* Mobile mic with animated voice quotes */}
              <div className="md:hidden mt-2 mb-10">
                <MobileVoiceDemo />
              </div>
              
              <p className="text-sm sm:text-base md:text-xl text-slate-300 mb-2 sm:mb-3 md:mb-4 max-w-lg leading-relaxed">
                Capture job details once. Structure them with AI. Move the job forward.
              </p>
              <p className="text-xs sm:text-sm md:text-base text-slate-400 mb-4 sm:mb-6 md:mb-8 max-w-lg border-l-2 border-orange-500 pl-3">
                Your on-call Contracts Administrator for keeping jobs in order and protecting your money.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4">
                <Button 
                  onClick={() => setShowSetupModal(true)} 
                  data-testid="hero-get-started"
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm px-4 py-2.5 sm:px-5 sm:py-3 md:px-8 md:py-6 text-xs sm:text-sm md:text-lg font-bold tracking-wide shadow-[4px_4px_0px_rgba(249,115,22,0.3)]"
                >
                  REGISTER INTEREST
                  <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 ml-1.5 sm:ml-2" />
                </Button>
                <Button 
                  onClick={() => navigate('/login')} 
                  variant="outline" 
                  data-testid="hero-login"
                  className="rounded-sm px-4 py-2.5 sm:px-5 sm:py-3 md:px-8 md:py-6 text-xs sm:text-sm md:text-lg font-semibold border-2 border-white/30 text-white hover:bg-white/10 tracking-wide"
                >
                  LOG IN
                </Button>
              </div>
            </motion.div>
            
            {/* Right: Desktop Microphone Visual - hidden on mobile */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden md:flex justify-start md:pl-4 lg:pl-16 self-center"
            >
              <div className="relative">
                {/* Main Mic Icon */}
                <div className="relative">
                  <motion.div 
                    className="w-12 h-12 sm:w-20 sm:h-20 md:w-44 md:h-44 lg:w-52 lg:h-52 bg-orange-500 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-[3px_3px_0px_rgba(0,0,0,0.3)] sm:shadow-[5px_5px_0px_rgba(0,0,0,0.3)] md:shadow-[8px_8px_0px_rgba(0,0,0,0.3)]"
                    animate={{ scale: [1, 1.02, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Mic className="w-6 h-6 sm:w-10 sm:h-10 md:w-24 md:h-24 lg:w-28 lg:h-28 text-white" strokeWidth={1.5} />
                  </motion.div>
                  
                  {/* Pulse rings */}
                  <motion.div 
                    className="absolute inset-0 bg-orange-500/20 rounded-lg sm:rounded-xl md:rounded-2xl"
                    animate={{ scale: [1, 1.3], opacity: [0.5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                  <motion.div 
                    className="absolute inset-0 bg-orange-500/10 rounded-lg sm:rounded-xl md:rounded-2xl"
                    animate={{ scale: [1, 1.5], opacity: [0.3, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
                  />
                </div>
                
                {/* Corner accents - hidden on mobile for cleaner look */}
                <div className="hidden md:block absolute -top-3 -left-3 w-6 h-6 border-t-2 border-l-2 border-orange-400"></div>
                <div className="hidden md:block absolute -top-3 -right-3 w-6 h-6 border-t-2 border-r-2 border-orange-400"></div>
                <div className="hidden md:block absolute -bottom-3 -left-3 w-6 h-6 border-b-2 border-l-2 border-orange-400"></div>
                <div className="hidden md:block absolute -bottom-3 -right-3 w-6 h-6 border-b-2 border-r-2 border-orange-400"></div>
                
                {/* Floating label - hidden on mobile */}
                <div className="hidden md:block absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <p className="text-sm font-bold text-orange-400 tracking-widest">TAP. TALK. DONE.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer"
          onClick={() => document.getElementById('problem-section')?.scrollIntoView({ behavior: 'smooth' })}
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <span className="text-white/60 text-xs font-medium tracking-wide">SCROLL</span>
          <ChevronDown className="w-5 h-5 text-orange-500" />
        </motion.div>
      </section>

      {/* Problem Section - Clean Dark Banner */}
      <section id="problem-section" className="py-12 sm:py-16 md:py-20 px-4 bg-[#1F2A37]">
        <div className="max-w-5xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 text-orange-400 text-[10px] sm:text-xs md:text-sm font-bold tracking-widest mb-4 sm:mb-6">
              <Brain className="w-4 h-4 sm:w-5 sm:h-5" />
              THE PROBLEM
            </div>
            <h2 
              className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-black text-white mb-4 sm:mb-6 leading-tight tracking-tight"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              TOO MUCH JOB INFO<br />
              <span className="text-orange-400">LIVES IN YOUR HEAD</span>
            </h2>
            <p className="text-slate-300 text-xs sm:text-sm md:text-lg leading-relaxed max-w-2xl mx-auto mb-8 sm:mb-10">
              Conversations at the ute. Photos on your phone. Promises made on site. Notes on scraps of paper. 
              When it comes time to quote, invoice, or sort out a dispute — where is it all?
            </p>
            
            {/* Pain Points - Horizontal */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 md:gap-10">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                </div>
                <span className="text-slate-300 text-xs sm:text-sm font-medium">Leads forgotten</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <ClipboardCheck className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                </div>
                <span className="text-slate-300 text-xs sm:text-sm font-medium">Records scattered</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <Banknote className="w-4 h-4 sm:w-5 sm:h-5 text-orange-400" />
                </div>
                <span className="text-slate-300 text-xs sm:text-sm font-medium">Money left behind</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Win / Deliver / Get Paid - With App Screenshots */}
      <section id="features" className="pt-12 sm:pt-16 md:pt-28 pb-0 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-6 sm:mb-10 md:mb-16">
            <div className="inline-flex items-center gap-2 text-orange-500 text-[10px] sm:text-xs md:text-sm font-bold tracking-widest mb-4 sm:mb-6">
              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
              THE SOLUTION
            </div>
            <h2 
              className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-black text-slate-900 mb-4 sm:mb-6 leading-tight tracking-tight"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              YOUR ON-CALL<br />
              <span className="text-orange-500">CONTRACTS ADMINISTRATOR</span>
            </h2>
            <p className="text-slate-600 text-xs sm:text-sm md:text-lg leading-relaxed max-w-2xl mx-auto">
              2Tap gives you one place to capture everything — leads, site updates, claims, and more. 
              Voice-first, mobile-ready, and built for tradies who'd rather be on the tools than stuck in admin.
            </p>
          </div>

          <div className="space-y-0">
            {/* WIN WORK */}
            <div className="relative py-12 sm:py-16 md:py-20">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-emerald-50/30" style={{ left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw', width: '100vw' }}></div>
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="relative grid md:grid-cols-2 gap-6 md:gap-12 items-center max-w-6xl mx-auto"
              >
                {/* Phone Mockup */}
                <div className="order-2 md:order-1 flex justify-center">
                  <div className="relative">
                    <AnimatedPhoneMockup 
                      imageSrc="https://customer-assets.emergentagent.com/job_tapwork/artifacts/94a33vl1_Screen%20Shot%202026-05-29%20at%2010.02.34%20pm.png"
                      imageAlt="Win Work - Lead & Referral Capture"
                      typingText="Met with Sarah at the new build site. She wants a quote for full electrical fit-out, 4 bedroom house..."
                      accentColor="bg-emerald-500"
                    />
                    {/* Decorative elements */}
                    <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-emerald-500/20 rounded-full blur-2xl"></div>
                    <div className="absolute -top-4 -left-4 w-16 h-16 bg-emerald-400/30 rounded-full blur-xl"></div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="order-1 md:order-2">
                  <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-xs sm:text-sm font-bold mb-4">
                    <Trophy className="w-4 h-4" />
                    WIN WORK
                  </div>
                  <h3 
                    className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 mb-4 tracking-tight"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    TURN LEADS INTO<br />
                    <span className="text-emerald-500">WORK</span>
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base md:text-lg mb-6">
                    Leads, referrals, site enquiries and quote details — all in one place. Never lose another opportunity.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {['Lead & referral capture', 'Generate scope', 'Photos & voice notes', 'Quote follow-ups'].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-slate-700 text-xs sm:text-sm">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* DELIVER WORK */}
            <div className="relative py-12 sm:py-16 md:py-20">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-50/30" style={{ left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw', width: '100vw' }}></div>
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="relative grid md:grid-cols-2 gap-6 md:gap-12 items-center max-w-6xl mx-auto"
              >
                {/* Content */}
                <div>
                  <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-xs sm:text-sm font-bold mb-4">
                    <Truck className="w-4 h-4" />
                    DELIVER WORK
                  </div>
                  <h3 
                    className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 mb-4 tracking-tight"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    TRACK EVERY<br />
                    <span className="text-blue-500">SITE UPDATE</span>
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base md:text-lg mb-6">
                    Keep site and job records organised while the work is happening. Nothing falls through the cracks.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {['Site updates & diary', 'SWMS & compliance', 'Photos & progress', 'Variations capture'].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-slate-700 text-xs sm:text-sm">
                        <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Phone Mockup */}
                <div className="flex justify-center">
                  <div className="relative">
                    <AnimatedPhoneMockup 
                      imageSrc="https://customer-assets.emergentagent.com/job_tapwork/artifacts/inv5ok13_Screen%20Shot%202026-05-29%20at%2010.02.22%20pm.png"
                      imageAlt="Deliver Work - Site Updates"
                      typingText="Finished installing the main switchboard today. Waiting on the plumber to complete rough-in before we can continue..."
                      accentColor="bg-blue-500"
                    />
                    {/* Decorative elements */}
                    <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl"></div>
                    <div className="absolute -top-4 -right-4 w-16 h-16 bg-blue-400/30 rounded-full blur-xl"></div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* GET PAID */}
            <div className="relative py-12 sm:py-16 md:py-20">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-orange-50/30" style={{ left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw', width: '100vw' }}></div>
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="relative grid md:grid-cols-2 gap-6 md:gap-12 items-center max-w-6xl mx-auto"
              >
                {/* Phone Mockup */}
                <div className="order-2 md:order-1 flex justify-center">
                  <div className="relative">
                    <AnimatedPhoneMockup 
                      imageSrc="https://customer-assets.emergentagent.com/job_tapwork/artifacts/td5rrstn_Screen%20Shot%202026-05-29%20at%2010.02.44%20pm.png"
                      imageAlt="Get Paid - Claims & Invoices"
                      typingText="Builder requested extra power points in the garage. That's a variation, need to send a quote for the additional work..."
                      accentColor="bg-orange-500"
                    />
                    {/* Decorative elements */}
                    <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-orange-500/20 rounded-full blur-2xl"></div>
                    <div className="absolute -top-4 -left-4 w-16 h-16 bg-orange-400/30 rounded-full blur-xl"></div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="order-1 md:order-2">
                  <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-3 py-1.5 rounded-full text-xs sm:text-sm font-bold mb-4">
                    <Banknote className="w-4 h-4" />
                    GET PAID
                  </div>
                  <h3 
                    className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 mb-4 tracking-tight"
                    style={{ fontFamily: 'Chivo, sans-serif' }}
                  >
                    PROTECT YOUR<br />
                    <span className="text-orange-500">MONEY</span>
                  </h3>
                  <p className="text-slate-600 text-sm sm:text-base md:text-lg mb-6">
                    Keep invoice, claim and payment follow-up records clear. Don't leave money on the table.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {['Claims & invoices', 'Payment tracking', 'Send reminders', 'Dispute support'].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-slate-700 text-xs sm:text-sm">
                        <CheckCircle2 className="w-4 h-4 text-orange-500 flex-shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Technical Timeline */}
      <section id="how-it-works" className="py-12 sm:py-16 md:py-28 px-4 bg-[#1F2A37]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 text-orange-400 px-3 py-1.5 sm:px-4 sm:py-2 rounded-sm text-[10px] sm:text-xs md:text-sm font-bold tracking-widest mb-3 sm:mb-4">
              SIMPLE BY DESIGN
            </div>
            <h2 
              className="text-xl sm:text-2xl md:text-4xl font-black text-white mb-3 sm:mb-4 tracking-tight"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              TAP. TALK. CAPTURE. DONE.
            </h2>
            <p className="text-xs sm:text-sm md:text-lg text-slate-300 max-w-2xl mx-auto">
              Nothing stays in your head. Capture it once, and 2Tap handles the rest.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {[
              { num: '01', title: 'CAPTURE', desc: 'Voice, text or photo. Get it out of your head.', icon: Mic },
              { num: '02', title: 'ORGANISE', desc: 'AI structures your notes into clear records.', icon: ClipboardCheck },
              { num: '03', title: 'CREATE', desc: 'Turn records into quotes, SWMS, invoices.', icon: FileText },
              { num: '04', title: 'SEND & SAVE', desc: 'Share with clients. Follow up. Get paid.', icon: Send },
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative"
              >
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-3 sm:p-4 md:p-6 h-full">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-black text-orange-500/30 mb-2 sm:mb-3 md:mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {item.num}
                  </div>
                  <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-orange-500/20 rounded-lg flex items-center justify-center mb-2 sm:mb-3 md:mb-4">
                    <item.icon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-orange-400" />
                  </div>
                  <h3 className="text-xs sm:text-sm md:text-lg font-bold text-white mb-1 sm:mb-2 tracking-wide" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {item.title}
                  </h3>
                  <p className="text-slate-400 text-[10px] sm:text-xs md:text-sm">{item.desc}</p>
                </div>
                {i < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-slate-600">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Marquee - Clean & Elegant */}
      <section className="py-10 sm:py-12 md:py-20 bg-slate-50 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 mb-6 sm:mb-8 md:mb-10">
          <h2 
            className="text-lg sm:text-xl md:text-3xl font-black text-slate-900 tracking-tight text-center"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            EVERYTHING YOU NEED. <span className="text-orange-500">NOTHING YOU DON'T.</span>
          </h2>
        </div>
        
        {/* Animated Feature Ticker */}
        <div className="relative">
          <motion.div 
            className="flex whitespace-nowrap"
            animate={{ x: ['0%', '-50%'] }}
            transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
          >
            {[
              'Voice Capture', 'Leads & Referrals', 'Site Updates', 'SWMS & Compliance', 
              'Quotes & Scope', 'Invoices & Claims', 'Follow-up Tasks', 'Job Handover',
              'Voice Capture', 'Leads & Referrals', 'Site Updates', 'SWMS & Compliance', 
              'Quotes & Scope', 'Invoices & Claims', 'Follow-up Tasks', 'Job Handover'
            ].map((item, i) => (
              <span 
                key={i} 
                className="mx-4 sm:mx-6 md:mx-10 text-sm sm:text-base md:text-xl font-semibold text-slate-400"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                {item}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 sm:py-16 md:py-28 px-4 bg-[#0f172a]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-xl sm:text-2xl md:text-4xl font-black text-white mb-3 sm:mb-4 tracking-tight"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              READY TO GET YOUR<br />
              <span className="text-orange-400">OFFICE IN YOUR POCKET?</span>
            </h2>
            <p className="text-xs sm:text-sm md:text-lg text-slate-300 mb-6 sm:mb-8 md:mb-10 max-w-xl mx-auto">
              Stop letting job details live in your head. Start capturing, organising and protecting your money.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 justify-center">
              <Button 
                onClick={() => setShowSetupModal(true)} 
                data-testid="cta-get-started"
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm px-6 py-3 sm:px-8 sm:py-4 md:px-10 md:py-6 text-xs sm:text-sm md:text-lg font-bold tracking-wide shadow-[4px_4px_0px_rgba(249,115,22,0.3)]"
              >
                REGISTER INTEREST
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
              </Button>
              <Button 
                onClick={() => navigate('/login')} 
                variant="outline" 
                data-testid="cta-login"
                className="rounded-sm px-6 py-3 sm:px-8 sm:py-4 md:px-10 md:py-6 text-xs sm:text-sm md:text-lg font-semibold border-2 border-slate-600 text-white hover:bg-slate-800 tracking-wide"
              >
                LOG IN
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 bg-slate-950">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-4">
          <button 
            onClick={() => setShowSetupModal(true)}
            className="text-slate-400 hover:text-orange-400 text-sm transition-colors"
          >
            Want a custom 2Tap for your business or industry? <span className="underline">Let's talk →</span>
          </button>
          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © 2025 2Tap. Powered for Smart Business.
            </p>
            <div className="flex items-center gap-6">
              <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-slate-400 hover:text-white text-sm transition-colors">Home</button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/features'); }} className="text-slate-400 hover:text-white text-sm transition-colors">Features</button>
              <button onClick={() => setShowShareModal(true)} className="text-slate-400 hover:text-white text-sm transition-colors">Share</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Share Modal */}
      <ShareModal open={showShareModal} onClose={() => setShowShareModal(false)} />
      
      {/* Request Setup Modal */}
      <RequestSetupModal isOpen={showSetupModal} onClose={() => setShowSetupModal(false)} />
    </div>
  );
};

export default LandingPage;
